/** @jsx jsx */
import { ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import {
  useTheme,
  Grid,
  Typography,
  Select,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import { ICustomTheme, IChallengesPartial } from 'interfaces';
import Button from 'components/Button';
import useQuery from 'core/utils/use-query.hook';
import { DECLARATION_FILTERS } from '../constants';
import ActionPeriod from './ActionPeriod';
import ROUTES from 'constants/routes';
import { StyledBubble, StyledVisibilityIcon } from '../styles';

const Title = styled(Typography)(({ theme }: { theme: ICustomTheme }) => ({
  display: 'block',
  marginBottom: theme.spacing(0.5),
  fontSize: 24,
  fontWeight: theme.typography.fontWeightBold,
  '&:first-letter': { textTransform: 'capitalize' },
}));

const ActionDates = styled(ActionPeriod)(
  ({
    theme,
    isMobile,
    isActionOnGoing,
  }: {
    theme: ICustomTheme;
    isMobile: boolean;
    isActionOnGoing: boolean;
  }) => ({
    ...(isMobile && { fontSize: theme.typography.caption.fontSize }),
    color: theme.colors.white,
    backgroundColor: isActionOnGoing
      ? theme.colors.paletteFromBO.primary
      : theme.colors.grey.light,
    width: 'fit-content',
    padding: theme.spacing(0.3, 1.5),
    borderRadius: theme.shape.borderRadius,
  }),
);

function ActionInformations({
  action,
  isDeclarationAvailable,
}: {
  action: IChallengesPartial;
  isDeclarationAvailable: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid
      container
      justifyContent={isMobile ? 'center' : 'space-between'}
      alignItems="center"
      css={{
        margin: theme.spacing(isMobile ? 2 : 0, 0, 4, 0),
        gap: theme.spacing(2, 2.5),
      }}
    >
      <Grid item>
        <RouterLink
          to={`${ROUTES.CHALLENGE}/${action.id}`}
          css={(theme: ICustomTheme) => ({
            display: 'flex',
            color: theme.colors.paletteFromBO.main,
            opacity: 0.8,
            alignItems: 'center',
            '&:hover': {
              opacity: 1,
            },
          })}
        >
          {!isMobile && <StyledVisibilityIcon />}
          <Title align={isMobile ? 'center' : 'left'}>
            {`${action.title} - ${action.id}`}
          </Title>
        </RouterLink>
        <ActionDates
          css={{ ...(isMobile && { margin: '0 auto' }) }}
          startDate={action.startDate}
          endDate={action.endDate}
          isMobile={isMobile}
          isActionOnGoing={action.isActionOnGoing}
        />
      </Grid>
      {!isMobile && isDeclarationAvailable && (
        <Grid item>
          <Button
            component={RouterLink}
            to={`${ROUTES.CHALLENGE}/${action.id}${ROUTES.DECLARE}`}
            css={{ width: 'auto', margin: 0 }}
            color="secondary"
          >
            {t('challenges.declaration.history.made_a_declaration')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
}

function DeclarationLegendType({
  type,
  quantity,
  bubbleProps,
}: {
  type: string;
  quantity: number;
  bubbleProps: { css: {} };
}): JSX.Element {
  const theme: ICustomTheme = useTheme();

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      css={{ gap: theme.spacing(1) }}
    >
      <Grid item>
        <StyledBubble {...bubbleProps}>{quantity}</StyledBubble>
      </Grid>
      <Grid item>
        <Typography
          align="center"
          css={{ fontSize: theme.typography.caption.fontSize }}
        >
          {type}
        </Typography>
      </Grid>
    </Grid>
  );
}

function DeclarationLegend({
  action,
}: {
  action: IChallengesPartial;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  return (
    <Grid
      container
      justifyContent={isMobile ? 'center' : 'flex-start'}
      alignItems="center"
      css={{ margin: theme.spacing(2, 0), gap: theme.spacing(2) }}
    >
      <Grid item>
        <DeclarationLegendType
          type={t('challenges.declaration.history.validated', {
            count: action.countDeclarationsValidated,
          })}
          quantity={action.countDeclarationsValidated}
          bubbleProps={{
            css: { backgroundColor: theme.colors.success.main },
          }}
        />
      </Grid>
      <Grid item>
        <DeclarationLegendType
          type={t('challenges.declaration.history.rejected', {
            count: action.countDeclarationsRefused,
          })}
          quantity={action.countDeclarationsRefused}
          bubbleProps={{ css: { backgroundColor: theme.colors.error.main } }}
        />
      </Grid>
      <Grid item>
        <DeclarationLegendType
          type={t('challenges.declaration.history.being_moderated', {
            count: action.countDeclarationsWaiting,
          })}
          quantity={action.countDeclarationsWaiting}
          bubbleProps={{ css: { backgroundColor: theme.colors.grey.light } }}
        />
      </Grid>
    </Grid>
  );
}

const SelectInput = styled(Select)(({ theme }: { theme: ICustomTheme }) => ({
  margin: theme.spacing(1, 0, 2, 0),
  boxShadow: theme.shadows[1],
  maxWidth: 300,
  padding: theme.spacing(1.5),
  width: '100%',
  '& .MuiSelect-icon': {
    color: theme.colors.grey.dark,
    fontSize: theme.typography.h3.fontSize,
    marginRight: theme.spacing(1),
  },
}));

const DeclarationSelectType = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    textTransform: 'capitalize',
    fontSize: theme.typography.caption.fontSize,
  }),
);

function DeclarationTypesSelect(): JSX.Element {
  const { t } = useTranslation();
  const { type } = useQuery();
  const history = useHistory();

  const handleChange = (event: ChangeEvent<{ value: unknown }>) => {
    event.preventDefault();

    const type = event.target.value;

    history.push(`?type=${type}`);
  };

  return (
    <SelectInput
      value={type || DECLARATION_FILTERS.all}
      onChange={handleChange}
      IconComponent={KeyboardArrowDownIcon}
    >
      <MenuItem value={DECLARATION_FILTERS.all}>
        <DeclarationSelectType>
          {t('challenges.declaration.history.all')}
        </DeclarationSelectType>
      </MenuItem>
      <MenuItem value={DECLARATION_FILTERS.validated}>
        <DeclarationSelectType>
          {t('challenges.declaration.history.validated')}
        </DeclarationSelectType>
      </MenuItem>
      <MenuItem value={DECLARATION_FILTERS.refused}>
        <DeclarationSelectType>
          {t('challenges.declaration.history.rejected')}
        </DeclarationSelectType>
      </MenuItem>
      <MenuItem value={DECLARATION_FILTERS.waiting}>
        <DeclarationSelectType>
          {t('challenges.declaration.history.being_moderated')}
        </DeclarationSelectType>
      </MenuItem>
    </SelectInput>
  );
}

export default function Header({
  action,
  isDeclarationAvailable,
}: {
  action: IChallengesPartial;
  isDeclarationAvailable: boolean;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  return (
    <div css={{ marginBottom: theme.spacing(2) }}>
      <ActionInformations
        action={action}
        isDeclarationAvailable={isDeclarationAvailable}
      />
      <Typography
        css={{
          color: theme.colors.grey.dark,
          ...(isMobile && { marginTop: theme.spacing(2) }),
        }}
        align={isMobile ? 'center' : 'left'}
        variant="h4"
        dangerouslySetInnerHTML={{
          __html: t('challenges.declaration.history.declarations_made', {
            count: action.totalDeclaration,
          }),
        }}
      />
      <DeclarationLegend action={action} />
      <DeclarationTypesSelect />
    </div>
  );
}
